import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import {
  retryFixImage,
  downloadOriginalZip,
} from '@/features/orders/ordering/services/orderMediaService.js';
import './ImageFixModal.scss';

// 图片修复弹窗组件
const ImageFixModal = ({
  show,
  images = [],
  clientInfo = {},
  client_id,
  onClose,
}) => {
  const [fixImages, setFixImages] = useState([]);
  const [retryingIds, setRetryingIds] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null); // 新增预览图片状态

  // 初始化fixImages
  useEffect(() => {
    if (show) {
      setFixImages(
        (images || []).map((img) => {
          const hasFixedImage =
            img.fix_image_url && img.fix_image_url.trim() !== '';

          return {
            ...img,
            fixStatus: hasFixedImage ? 'success' : 'pending',
            error: null,
          };
        })
      );
      setRetryingIds([]);
      setDownloading(false);
    }
  }, [show, images]);

  // 单张修复
  const handleRetryFix = async (img) => {
    setRetryingIds((prev) => [...prev, img.id]);
    setFixImages((prev) =>
      prev.map((item) =>
        item.id === img.id
          ? { ...item, fixStatus: 'fixing', error: null }
          : item
      )
    );
    try {
      const res = await retryFixImage(img.content, img.id, client_id);
      if (res.success) {
        setFixImages((prev) =>
          prev.map((item) =>
            item.id === img.id
              ? {
                  ...item,
                  fixStatus: 'success',
                  fix_image_url: res.fixedUrl,
                  error: null,
                }
              : item
          )
        );
      } else {
        setFixImages((prev) =>
          prev.map((item) =>
            item.id === img.id
              ? { ...item, fixStatus: 'failed', error: res.error }
              : item
          )
        );
      }
    } catch (err) {
      setFixImages((prev) =>
        prev.map((item) =>
          item.id === img.id
            ? {
                ...item,
                fixStatus: 'failed',
                error: err?.message || '未知错误',
              }
            : item
        )
      );
    }
    setRetryingIds((prev) => prev.filter((id) => id !== img.id));
  };

  // 全部重试
  const handleRetryAll = async () => {
    const failedIds = fixImages
      .filter((img) => img.fixStatus !== 'success')
      .map((img) => img.id);
    setRetryingIds(failedIds);
    setFixImages((prev) =>
      prev.map((item) =>
        item.fixStatus !== 'success' ? { ...item, fixStatus: 'fixing' } : item
      )
    );
    if (failedIds.length === 0) {
      alert('所有图片都已修复成功！');
      return;
    }
    for (const img of fixImages) {
      if (img.fixStatus === 'success') continue;
      try {
        const res = await retryFixImage(img.content, img.id, client_id);
        if (res.success) {
          setFixImages((prev) =>
            prev.map((item) =>
              item.id === img.id
                ? {
                    ...item,
                    fixStatus: 'success',
                    fix_image_url: res.fixedUrl,
                    error: null,
                  }
                : item
            )
          );
        } else {
          setFixImages((prev) =>
            prev.map((item) =>
              item.id === img.id
                ? { ...item, fixStatus: 'failed', error: res.error }
                : item
            )
          );
        }
      } catch (err) {
        setFixImages((prev) =>
          prev.map((item) =>
            item.id === img.id
              ? {
                  ...item,
                  fixStatus: 'failed',
                  error: err?.message || '未知错误',
                }
              : item
          )
        );
      }
    }
    setRetryingIds([]);
  };

  // 下载原图
  const handleDownloadOriginal = (url) => {
    window.open(url, '_blank');
  };

  // 下载修复图及原图
  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const images = fixImages
        .filter((img) => img.fixStatus === 'success' && img.fix_image_url)
        .flatMap((img, index) => [
          {
            url: img.fix_image_url,
            name:
              img.zipNameFixed ||
              `${img.sort_order ? img.sort_order : '未排序' + (index + 1)}_${
                img.image_dynamic_AI === false ? '静态' : '动态'
              }_${`${img.description}` || `无描述`}_修复.jpg`,
          },
          {
            url: img.content,
            name:
              img.zipName ||
              `${img.sort_order ? img.sort_order : '未排序' + (index + 1)}_${
                img.image_dynamic_AI === false ? '静态' : '动态'
              }_${`${img.description}` || `无描述`}_原图.jpg`,
          },
        ]);
      if (images.length === 0) {
        alert('没有可下载的修复成功图片');
        setDownloading(false);
        return;
      }
      const zipBlob = await downloadOriginalZip(images, client_id);
      const url = window.URL.createObjectURL(
        new Blob([zipBlob], { type: 'application/zip' })
      );
      const link = document.createElement('a');
      link.href = url;
      const zipName = `${
        clientInfo.parkName || clientInfo.user_companyName || '园区'
      }_${clientInfo.clientName || ''}_${clientInfo.ceremonyDate || ''}_${
        clientInfo.videoType || ''
      }_原图及修复图.zip`;
      link.setAttribute('download', zipName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (e) {
      alert('下载失败，请稍后重试');
    }
    setDownloading(false);
  };

  // 下载全部原图
  const handleDownloadAllOriginal = async () => {
    setDownloading(true);
    try {
      const images = fixImages.map((img, index) => ({
        url: img.content,
        name:
          img.zipName ||
          `${img.sort_order ? img.sort_order : '未排序' + (index + 1)}_${
            img.image_dynamic_AI === false ? '静态' : '动态'
          }_${`${img.description}` || `无描述`}_原图.jpg`,
      }));
      const zipBlob = await downloadOriginalZip(images, client_id);
      const url = window.URL.createObjectURL(
        new Blob([zipBlob], { type: 'application/zip' })
      );
      const link = document.createElement('a');
      link.href = url;
      const zipName = `${
        clientInfo.parkName || clientInfo.user_companyName || '园区'
      }_${clientInfo.clientName || ''}_${clientInfo.ceremonyDate || ''}_${
        clientInfo.videoType || ''
      }_原图.zip`;
      link.setAttribute('download', zipName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (e) {
      alert('下载失败，请稍后重试');
    }
    setDownloading(false);
  };

  // 仅下载修复图片
  const handleDownloadFixedOnly = async () => {
    setDownloading(true);
    try {
      const images = fixImages
        .filter((img) => img.fixStatus === 'success' && img.fix_image_url)
        .map((img, index) => ({
          url: img.fix_image_url,
          name:
            img.zipNameFixed ||
            `${img.sort_order ? img.sort_order : '未排序' + (index + 1)}_${
              img.image_dynamic_AI === false ? '静态' : '动态'
            }_${`${img.description}` || `无描述`}_修复.jpg`,
        }));
      if (images.length === 0) {
        alert('没有可下载的修复成功图片');
        setDownloading(false);
        return;
      }
      const zipName = `${
        clientInfo.parkName || clientInfo.user_companyName || '园区'
      }_${clientInfo.clientName || ''}_${clientInfo.ceremonyDate || ''}_${
        clientInfo.videoType || ''
      }_修复图片.zip`;
      const zipBlob = await downloadOriginalZip(images, client_id);
      const url = window.URL.createObjectURL(
        new Blob([zipBlob], { type: 'application/zip' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', zipName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (e) {
      alert('下载失败，请稍后重试');
    }
    setDownloading(false);
  };

  // 新增：单张下载修复图
  const handleDownloadFixedImg = (img, index) => {
    const fileName =
      img.zipNameFixed ||
      `${img.sort_order ? img.sort_order : '未排序' + (index + 1)}_${
        img.image_dynamic_AI === false ? '静态' : '动态'
      }_${`${img.description}` || `无描述`}_修复.jpg`;
    const link = document.createElement('a');
    link.href = img.fix_image_url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Modal show={show} onHide={onClose} className="imges-download">
        <Modal.Header closeButton>
          <Modal.Title>图片修复</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fixImages.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                color: 'green',
                fontWeight: 'bold',
              }}
            >
              所有图片已修复成功！
            </div>
          ) : (
            fixImages.map((img, index) => (
              <div className="image-Container" key={img.id}>
                <img
                  src={img.content}
                  alt={img.description}
                  onClick={() => setPreviewImg(img.content)} // 点击放大
                />
                <div className="right-container">
                  <div className="description">
                    <strong>描述：</strong>
                    {img.description || '无'}
                  </div>
                  <div className="fix-status-container">
                    {img.fixStatus === 'pending' && (
                      <div className="pending-label">待修复</div>
                    )}
                    {img.fixStatus === 'fixing' && (
                      <div className="fixing-label">修复中...</div>
                    )}
                    {img.fixStatus === 'success' && (
                      <>
                        <div className="success-container">
                          <strong className="success-label">
                            修复成功
                            {img.fix_image_url && (
                              <span
                                style={{
                                  fontSize: '0.8em',
                                  color: '#666',
                                  marginLeft: '5px',
                                }}
                              >
                                (已保存)
                              </span>
                            )}
                          </strong>
                        </div>
                        <img
                          src={img.fix_image_url}
                          alt="修复后图片"
                          onClick={() => setPreviewImg(img.fix_image_url)}
                        />
                      </>
                    )}
                    {img.fixStatus === 'failed' && (
                      <div className="failed-label">
                        <strong>修复失败：</strong>
                        {img.error}
                      </div>
                    )}
                  </div>
                  <div className="btns-container">
                    {img.fixStatus === 'pending' ? (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleRetryFix(img)}
                        disabled={retryingIds.includes(img.id) || downloading}
                      >
                        {retryingIds.includes(img.id) ? '修复中...' : '修复'}
                      </Button>
                    ) : img.fixStatus === 'failed' ? (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleRetryFix(img)}
                        disabled={retryingIds.includes(img.id) || downloading}
                      >
                        {retryingIds.includes(img.id)
                          ? '修复中...'
                          : '重试修复'}
                      </Button>
                    ) : img.fixStatus === 'fixing' ? (
                      <Button size="sm" variant="outline-secondary" disabled>
                        修复中...
                      </Button>
                    ) : img.fixStatus === 'success' ? (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleRetryFix(img)}
                        disabled={retryingIds.includes(img.id) || downloading}
                      >
                        {retryingIds.includes(img.id)
                          ? '修复中...'
                          : '重新修复'}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleDownloadOriginal(img.url)}
                      disabled={downloading}
                    >
                      下载原图
                    </Button>
                    {img.fixStatus === 'success' && (
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => handleDownloadFixedImg(img, index)}
                        disabled={downloading}
                      >
                        下载修复图
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="footer-btns-container">
            <Button variant="link" onClick={onClose} disabled={downloading}>
              关闭
            </Button>
            <div className="imges-btns">
              <Button
                variant="primary"
                onClick={handleRetryAll}
                disabled={
                  fixImages.length === 0 ||
                  downloading ||
                  retryingIds.length > 0
                }
              >
                {retryingIds.length > 0
                  ? '图片修复中...'
                  : fixImages.filter((img) => img.fixStatus !== 'success')
                        .length === 0
                    ? '所有图片已修复'
                    : '修复剩余图片'}
              </Button>
              <div className="download-btns">
                <Button
                  variant="success"
                  onClick={handleDownloadAll}
                  disabled={downloading}
                >
                  {downloading ? '正在打包...' : '下载修复图及原图'}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleDownloadAllOriginal}
                  disabled={downloading}
                >
                  下载原图
                </Button>
                <Button
                  variant="success"
                  onClick={handleDownloadFixedOnly}
                  disabled={downloading}
                >
                  仅下载修复图片
                </Button>
              </div>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {/* 图片预览大图弹窗 */}
      <Modal
        show={!!previewImg}
        onHide={() => setPreviewImg(null)}
        centered
        size="xl"
        className="preview-fixed-img-container"
      >
        <Modal.Body className="preview-fixed-img">
          <img src={previewImg} alt="预览" />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ImageFixModal;
